"use client"

import {useEffect, useMemo, useRef, useState} from "react";
import {Button, InputAdornment, MenuItem, Slider, TextField} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import MediaDevicesUtils from "../../utils/MediaDevicesUtils";
import MediaDeviceInfoWithState from "../../utils/MediaDeviceInfoPlus";
import {getCompactDeviceName, getFriendlyDeviceName} from "../../utils/DeviceDisplayNames";
import "./InputMonitorTest.css";

interface InputMonitorTestProps {
    inputDeviceId: string;
    /** 0-200, from parent input volume slider */
    volume?: number;
    onVolumeChange?: (volume: number) => void;
    /** Render icon-only trigger for grouped test UI */
    iconOnly?: boolean;
    /** Optional language name without country, shown before title */
    languageName?: string;
}

type SinkAudioElement = HTMLAudioElement & {
    setSinkId?: (sinkId: string) => Promise<void>;
};

type AudioContextWithSink = AudioContext & {
    setSinkId?: (sinkId: string) => Promise<void>;
};

const PANEL_AUDIO_LEVEL_THRESHOLD = 60;

export default function InputMonitorTest({inputDeviceId, volume, onVolumeChange, iconOnly, languageName}: InputMonitorTestProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isMonitoring, setIsMonitoring] = useState(false);
    const [monitorVolume, setMonitorVolume] = useState<number>(volume ?? 100);
    const [audioLevel, setAudioLevel] = useState(0);
    const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const gainNodeRef = useRef<GainNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
    const monitorElementRef = useRef<SinkAudioElement | null>(null);
    const inputStreamRef = useRef<MediaStream | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const startupUnmuteTimeoutRef = useRef<number | null>(null);
    const monitorVolumeRef = useRef<number>(volume ?? 100);
    monitorVolumeRef.current = volume ?? monitorVolume;

    const shouldHideUsbCodecDevice = (label: string): boolean => {
        // Robust match for variants like "USB Audio Codec", "Usb audio codec", etc.
        return /usb\s*audio\s*codec/i.test(label);
    };

    const outputDevices = useMemo(() => {
        return MediaDevicesUtils.instance()
            .getDevices("audiooutput")
            .filter((d) => d.deviceId !== "communications")
            .filter((d) => {
                const rawLabel = d.label ?? "";
                const friendlyLabel = getFriendlyDeviceName(rawLabel);
                return !shouldHideUsbCodecDevice(rawLabel) && !shouldHideUsbCodecDevice(friendlyLabel);
            });
    }, [isOpen]);

    const selectedInputDeviceLabel = useMemo(() => {
        const inputDevices = MediaDevicesUtils.instance().getDevices("audioinput");
        const selected = inputDevices.find(d => d.deviceId === inputDeviceId);
        return selected ? getCompactDeviceName(selected.label) : "Unknown input device";
    }, [inputDeviceId, isOpen]);

    const outputLabelById = useMemo(() => {
        const byId: Record<string, string> = {};
        outputDevices.forEach(d => {
            byId[d.deviceId] = getCompactDeviceName(d.label);
        });
        return byId;
    }, [outputDevices]);

    useEffect(() => {
        if (!isOpen) return;
        if (!selectedOutputDeviceId && outputDevices.length > 0) {
            setSelectedOutputDeviceId(outputDevices[0].deviceId);
        }
    }, [isOpen, outputDevices, selectedOutputDeviceId]);

    // Mirror parent panel input volume into dialog slider (panel -> popup).
    useEffect(() => {
        setMonitorVolume(volume ?? 100);
    }, [volume]);

    // Keep monitor gain in sync when parent slider changes while dialog is open.
    useEffect(() => {
        if (gainNodeRef.current && audioContextRef.current) {
            gainNodeRef.current.gain.setValueAtTime((volume ?? 100) / 100, audioContextRef.current.currentTime);
        }
    }, [volume]);

    // While monitoring, allow live switching of the listening output device.
    useEffect(() => {
        const applySinkChange = async () => {
            try {
                if (!isMonitoring || !selectedOutputDeviceId || !audioContextRef.current) return;

                const audioContextWithSink = audioContextRef.current as AudioContextWithSink;
                if (typeof audioContextWithSink.setSinkId === "function") {
                    await audioContextWithSink.setSinkId(selectedOutputDeviceId);
                    return;
                }

                if (!monitorElementRef.current || typeof monitorElementRef.current.setSinkId !== "function") return;
                await monitorElementRef.current.setSinkId(selectedOutputDeviceId);
            } catch {
                setError("Could not switch listening device while test is running.");
            }
        };

        applySinkChange();
    }, [selectedOutputDeviceId, isMonitoring]);

    const stopMeter = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        setAudioLevel(0);
    };

    const cleanupAudio = () => {
        stopMeter();

        if (startupUnmuteTimeoutRef.current) {
            window.clearTimeout(startupUnmuteTimeoutRef.current);
            startupUnmuteTimeoutRef.current = null;
        }

        if (monitorElementRef.current) {
            monitorElementRef.current.pause();
            monitorElementRef.current.srcObject = null;
            monitorElementRef.current.remove();
            monitorElementRef.current = null;
        }

        if (sourceRef.current) {
            sourceRef.current.disconnect();
            sourceRef.current = null;
        }
        if (gainNodeRef.current) {
            gainNodeRef.current.disconnect();
            gainNodeRef.current = null;
        }
        if (analyserRef.current) {
            analyserRef.current.disconnect();
            analyserRef.current = null;
        }
        if (destinationRef.current) {
            destinationRef.current.disconnect();
            destinationRef.current = null;
        }

        if (inputStreamRef.current) {
            inputStreamRef.current.getTracks().forEach(t => t.stop());
            inputStreamRef.current = null;
        }

        if (audioContextRef.current && audioContextRef.current.state !== "closed") {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        setIsMonitoring(false);
    };

    const createMonitoringAudioContext = (preferredSampleRate?: number): AudioContext => {
        if (preferredSampleRate && Number.isFinite(preferredSampleRate)) {
            try {
                return new AudioContext({sampleRate: preferredSampleRate});
            } catch (error) {
                console.warn("[InputMonitorTest] Falling back to default AudioContext sample rate.", error);
            }
        }

        return new AudioContext();
    };

    const logMonitoringDiagnostics = (audioContext: AudioContext, inputStream: MediaStream, outputDeviceId: string) => {
        const audioTrack = inputStream.getAudioTracks()[0];
        const settings = audioTrack?.getSettings?.();

        console.info("[InputMonitorTest] Monitor diagnostics", {
            audioContextSampleRate: audioContext.sampleRate,
            inputTrackSampleRate: settings?.sampleRate ?? "unknown",
            inputTrackLatency: (settings as any)?.latency ?? "unknown",
            inputDeviceId: settings?.deviceId ?? inputDeviceId,
            outputDeviceId,
        });

        if (typeof settings?.sampleRate === "number" && settings.sampleRate !== audioContext.sampleRate) {
            console.warn("[InputMonitorTest] Sample-rate mismatch detected.", {
                audioContextSampleRate: audioContext.sampleRate,
                inputTrackSampleRate: settings.sampleRate,
            });
        }
    };

    useEffect(() => {
        return () => cleanupAudio();
    }, []);

    const startMeter = () => {
        if (!analyserRef.current) return;
        const analyser = analyserRef.current;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const update = () => {
            analyser.getByteFrequencyData(dataArray);

            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                sum += dataArray[i];
            }
            const average = sum / bufferLength;
            const percentage = average > PANEL_AUDIO_LEVEL_THRESHOLD
                ? 100
                : Math.round((average * 100) / PANEL_AUDIO_LEVEL_THRESHOLD);

            // UI meter expects 0..1 scale
            setAudioLevel(Math.max(0, Math.min(percentage / 100, 1)));
            animationFrameRef.current = requestAnimationFrame(update);
        };
        update();
    };

    const startMonitoring = async () => {
        setError(null);
        if (!inputDeviceId) {
            setError("No input device selected.");
            return;
        }
        if (!selectedOutputDeviceId) {
            setError("Select the device to hear the input audio.");
            return;
        }

        try {
            cleanupAudio();

            const inputStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: {exact: inputDeviceId},
                    sampleRate: {ideal: 48000},
                    channelCount: {ideal: 1},
                    autoGainControl: false,
                    noiseSuppression: false,
                    echoCancellation: false,
                },
            });
            inputStreamRef.current = inputStream;

            const inputTrackSettings = inputStream.getAudioTracks()[0]?.getSettings?.();
            const preferredSampleRate = typeof inputTrackSettings?.sampleRate === "number"
                ? inputTrackSettings.sampleRate
                : undefined;

            const audioContext = createMonitoringAudioContext(preferredSampleRate);
            audioContextRef.current = audioContext;
            await audioContext.resume();

            const audioContextWithSink = audioContext as AudioContextWithSink;
            if (typeof audioContextWithSink.setSinkId === "function") {
                await audioContextWithSink.setSinkId(selectedOutputDeviceId);
            }

            logMonitoringDiagnostics(audioContext, inputStream, selectedOutputDeviceId);

            const source = audioContext.createMediaStreamSource(inputStream);
            const gainNode = audioContext.createGain();
            gainNode.gain.value = (volume ?? 100) / 100;
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0.5;

            source.connect(gainNode);
            gainNode.connect(analyser);

            sourceRef.current = source;
            gainNodeRef.current = gainNode;
            analyserRef.current = analyser;

            if (typeof audioContextWithSink.setSinkId === "function") {
                gainNode.connect(audioContext.destination);
            } else {
                const destination = audioContext.createMediaStreamDestination();
                gainNode.connect(destination);
                destinationRef.current = destination;

                const monitorEl = document.createElement("audio") as SinkAudioElement;
                monitorEl.autoplay = true;
                monitorEl.muted = true;
                monitorEl.srcObject = destination.stream;

                if (typeof monitorEl.setSinkId === "function") {
                    await monitorEl.setSinkId(selectedOutputDeviceId);
                } else {
                    setError("Your browser does not support selecting output device for monitor.");
                    cleanupAudio();
                    return;
                }

                await monitorEl.play();
                monitorElementRef.current = monitorEl;
                startupUnmuteTimeoutRef.current = window.setTimeout(() => {
                    if (monitorElementRef.current === monitorEl) {
                        monitorEl.muted = false;
                    }
                    startupUnmuteTimeoutRef.current = null;
                }, 250);
            }

            setIsMonitoring(true);
            startMeter();
        } catch (e) {
            cleanupAudio();
            setError("Could not start input monitor. Check microphone and output device permissions.");
        }
    };

    const stopMonitoring = () => {
        cleanupAudio();
    };

    const toggleDialog = () => {
        if (isOpen) {
            cleanupAudio();
        }
        setError(null);
        setIsOpen(!isOpen);
    };

    const onVolumeSliderChange = (_: Event, value: number | number[]) => {
        const next = value as number;
        setMonitorVolume(next);
        monitorVolumeRef.current = next;
        if (gainNodeRef.current && audioContextRef.current) {
            gainNodeRef.current.gain.setValueAtTime(next / 100, audioContextRef.current.currentTime);
        }
        onVolumeChange?.(next);
    };

    return (
        <div className="input-monitor-test-container">
            <Button
                className="base-button input-monitor-test-trigger"
                onClick={toggleDialog}
                startIcon={iconOnly ? undefined : <MicIcon className="input-monitor-test-icon"/>}
                aria-label="Input monitor test"
            >
                {/*{iconOnly ? <MicIcon className="input-monitor-test-icon"/> : "Monitor"}*/}
                {iconOnly ? <MicIcon className="input-monitor-test-icon"/> : "Test"}
            </Button>

            {isOpen && (
                <div className="input-monitor-test-dialog-backdrop">
                    <div className="input-monitor-test-dialog" role="dialog" aria-labelledby="input-monitor-test-title">
                        <div className="input-monitor-test-dialog-header">
                            <h2 id="input-monitor-test-title">{languageName ? `${languageName} - ` : ""}Input Audio Test</h2>
                            <div className="input-monitor-test-header-info">
                                <div className="input-monitor-test-input-label">Monitoring audio from input device below:</div>
                                <div className="input-monitor-test-input-device">
                                    <MicIcon fontSize="small"/>
                                    <span>{selectedInputDeviceLabel}</span>
                                </div>
                            </div>
                            <button className="input-monitor-test-close-button" onClick={toggleDialog} aria-label="Close">
                                x
                            </button>
                        </div>

                        <div className="input-monitor-test-dialog-content">
                            {error ? (
                                <div className="input-monitor-test-error">{error}</div>
                            ) : null}

                            <div className="input-monitor-test-output-select">
                                <div className="input-monitor-test-output-label">
                                    <span>Select where to play this test</span>
                                </div>
                                <TextField
                                    className="input-monitor-test-output-field"
                                    select
                                    size="small"
                                    fullWidth
                                    value={selectedOutputDeviceId}
                                    onChange={(e) => setSelectedOutputDeviceId(e.target.value)}
                                    SelectProps={{
                                        renderValue: (value) => outputLabelById[String(value)] ?? String(value),
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VolumeUpIcon fontSize="small"/>
                                            </InputAdornment>
                                        ),
                                    }}
                                >
                                    {outputDevices.map((d: MediaDeviceInfoWithState) => (
                                        <MenuItem key={d.deviceId} value={d.deviceId}>
                                            {getFriendlyDeviceName(d.label)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>

                            <div className="input-monitor-test-controls">
                                <Button
                                    variant={isMonitoring ? "outlined" : "contained"}
                                    color={isMonitoring ? "error" : "primary"}
                                    onClick={isMonitoring ? stopMonitoring : startMonitoring}
                                >
                                    {isMonitoring ? "Stop test" : "Start test"}
                                </Button>
                            </div>

                            <div className="input-monitor-test-volume-control">
                                <div className="input-monitor-test-volume-label">Volume</div>
                                <Slider
                                    aria-label="Input monitor volume"
                                    value={monitorVolume}
                                    min={0}
                                    max={200}
                                    step={10}
                                    onChange={onVolumeSliderChange}
                                />
                            </div>

                            <div className="input-monitor-test-level-container">
                                <div className="input-monitor-test-level-label">Audio Level:</div>
                                <div className="input-monitor-test-level-meter">
                                    <div
                                        className="input-monitor-test-level-fill"
                                        style={{transform: `scaleX(${Math.max(0, Math.min(audioLevel, 1))})`}}
                                    />
                                </div>
                                <div className="input-monitor-test-level-value">{Math.round(audioLevel * 100)}%</div>
                            </div>
                        </div>

                        <div className="input-monitor-test-dialog-footer">
                            <Button variant="outlined" onClick={toggleDialog}>
                                Done
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

